/* eslint-disable @next/next/no-img-element */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { FunctionComponent, useEffect, useState } from 'react';

import {
  EuiBreadcrumb,
  EuiHorizontalRule,
  EuiPageTemplate,
  EuiPageTemplateProps,
  EuiSpacer,
  EuiText,
  euiPaletteForStatus,
  EuiFlyout,
  EuiFlyoutBody,
  useIsWithinBreakpoints,
  EuiButtonIcon,
} from '@elastic/eui';
import { useRouter } from 'next/router';
import { HeaderPrimary } from '@components/header/header-primary';
import { HeaderSecondary } from '@components/header/header-secondary';
import Spinner from '@components/spinner/spinner';
import { Roles } from '@lib/domain/auth';
import Announcement from '@components/announcement';
import { useSession } from 'next-auth/react';
import { css } from '@emotion/react';

export type Props = {
  breadcrumb?: EuiBreadcrumb[];
  alignment?: 'top' | 'center' | 'horizontalCenter';
  panelled?: boolean;
  restrictWidth?: string | number | boolean;
  pageTitle?: string;
  showSpinner?: boolean;
  role?: Roles;
  sidebar?: any;
  sidebarWidth?: number;
  hideFlyout?: boolean;
  customConstraint?: string;
};

const MainLayout: FunctionComponent<EuiPageTemplateProps & Props> = ({
  children,
  breadcrumb,
  pageTitle,
  alignment,
  panelled,
  restrictWidth = 800,
  showSpinner,
  sidebar,
  sidebarWidth,
  customConstraint,
  hideFlyout = false,
  ...rest
}) => {
  const router = useRouter();
  const { data: session } = useSession();
  const hasFeature = (feature: string) => session?.features.includes(feature);
  const [showSubHeader, setShowSubHeader] = useState(false);
  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
  const isMobile = useIsWithinBreakpoints(['xs', 's', customConstraint]);

  useEffect(() => {
    setShowSubHeader(
      router.route !== '/' &&
        router.route !== '/403' &&
        router.route !== '/error' &&
        !router.route.startsWith('/auth')
    );
  }, [router, router.route]);

  const renderPageTitle = (
    <>
      <EuiText size="xs">
        <h2>{pageTitle}</h2>
      </EuiText>
      <EuiHorizontalRule margin="m" color="lightgray" />
    </>
  );
  const palette = euiPaletteForStatus(5);

  return (
    <div css={{ position: 'relative' }}>
      <Spinner show={showSpinner} />

      <EuiPageTemplate panelled={panelled} restrictWidth={true} {...rest}>
        <HeaderPrimary />

        {showSubHeader ? <HeaderSecondary breadcrumb={breadcrumb} /> : null}
        {sidebar && isMobile && !hideFlyout && (
          <EuiButtonIcon
            color="primary"
            size="m"
            display="fill"
            style={{ margin: 16 }}
            iconType="filter"
            onClick={() => setIsFlyoutVisible(!isFlyoutVisible)}
          />
        )}
        {sidebar &&
          (isMobile && !hideFlyout ? (
            isFlyoutVisible && (
              <EuiFlyout
                side="left"
                ownFocus
                onClose={() => setIsFlyoutVisible(false)}>
                <EuiFlyoutBody>{sidebar}</EuiFlyoutBody>
              </EuiFlyout>
            )
          ) : (
            <EuiPageTemplate.Sidebar
              minWidth={sidebarWidth ?? 400}
              sticky={true}
              paddingSize="s">
              {sidebar}
            </EuiPageTemplate.Sidebar>
          ))}

        {hasFeature('announce-error') && session?.announceError && (
          <div
            style={{
              position: 'sticky',
              top: showSubHeader ? '96px' : '48px',
              zIndex: 999,
            }}>
            <Announcement
              backgroundColor={palette[4]}
              textColor="white"
              message={session?.announceError}
            />
          </div>
        )}

        {hasFeature('announce-warning') && session?.announceWarning && (
          <Announcement
            backgroundColor={palette[2]}
            textColor="black"
            message={session?.announceWarning}
          />
        )}

        {hasFeature('announce-info') && session?.announceInfo && (
          <Announcement
            backgroundColor={palette[1]}
            textColor="black"
            message={session?.announceInfo}
          />
        )}

        {hasFeature('announce-new-version') && session?.announceNewVersion && (
          <Announcement
            backgroundColor={palette[1]}
            textColor="black"
            message={session?.announceNewVersion}
            showButton={true}
          />
        )}

        <EuiSpacer size="s" />

        <EuiPageTemplate.Section
          grow={true}
          paddingSize="m"
          restrictWidth={restrictWidth}
          alignment={alignment || 'top'}
          css={css`
            .eui-15sbasf-euiPageSection__content-m {
              height: ${isMobile ? 'calc(100vh - 220px)' : '100%'};
            }
          `}>
          {pageTitle ? renderPageTitle : null}
          {children}
        </EuiPageTemplate.Section>
      </EuiPageTemplate>
    </div>
  );
};

export default MainLayout;
